'use client';

import { createLogger } from '@/lib/utils/logger';

const logger = createLogger('auth:session-cleanup');

/**
 * Identifies and clears legacy auth tokens and cookies
 * This should be run once on application initialization to ensure clean auth state
 */
export function cleanupLegacyAuthTokens(): boolean {
  try {
    logger.info('Running legacy auth token cleanup');
    let cleanupPerformed = false;
    
    // Get the project ref from environment or fallback to hardcoded value
    const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL!;
    const PROJECT_REF = SUPABASE_URL.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1] || 'lpjvndsvkvqgkdkvacnn';
    
    // Get proper cookie name format
    const COOKIE_NAME = `sb-${PROJECT_REF}-auth-token`;
    
    // 1. Clear localStorage tokens with legacy formats
    try {
      // Check for localStorage tokens
      const storedToken = localStorage.getItem(COOKIE_NAME);
      
      if (storedToken) {
        // Check if it's in the legacy base64 format
        if (storedToken.includes('base64-eyJ')) {
          logger.debug('Found legacy base64 token in localStorage, removing');
          localStorage.removeItem(COOKIE_NAME);
          cleanupPerformed = true;
        }
        
        // Check if it's not proper JSON
        try {
          JSON.parse(storedToken);
        } catch (e) {
          // Not valid JSON, remove it
          logger.debug('Found invalid JSON token in localStorage, removing');
          localStorage.removeItem(COOKIE_NAME);
          cleanupPerformed = true;
        }
      }
      
      // Also clear any legacy token storage keys we might have used
      const legacyKeys = [
        'auth_token',
        'auth_refresh_token',
        'auth_expires_at',
        'last_token_refresh',
        'auth_access_token',
        'auth_profile',
        'auth_login_time'
      ];
      
      legacyKeys.forEach(key => {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
          cleanupPerformed = true;
        }
      });
    } catch (e) {
      logger.warn('Error accessing localStorage', e);
    }
    
    // 2. Clear cookies with legacy formats
    try {
      const cookies = document.cookie.split(';');
      
      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        
        if (name === COOKIE_NAME) {
          // Try to decode the cookie value
          try {
            const decodedValue = decodeURIComponent(value);
            
            // Check if it's in legacy format
            if (decodedValue.includes('base64-eyJ')) {
              logger.debug('Found legacy base64 cookie, removing');
              
              // Clear the cookie by setting an expired date
              const cookieDomain = window.location.hostname.includes('apprendo.co') ? '.apprendo.co' : undefined;
              const cookieSettings = cookieDomain ? `; domain=${cookieDomain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; secure; samesite=none` : '; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; secure';
              
              document.cookie = `${name}=${cookieSettings}`;
              cleanupPerformed = true;
            }
            
            // Also try parsing it as JSON to check validity
            try {
              JSON.parse(decodedValue);
            } catch (e) {
              // Not valid JSON, clear it
              logger.debug('Found invalid JSON cookie, removing');
              
              const cookieDomain = window.location.hostname.includes('apprendo.co') ? '.apprendo.co' : undefined;
              const cookieSettings = cookieDomain ? `; domain=${cookieDomain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; secure; samesite=none` : '; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; secure';
              
              document.cookie = `${name}=${cookieSettings}`;
              cleanupPerformed = true;
            }
          } catch (e) {
            // If we can't decode it, it's probably malformed
            logger.debug('Found malformed cookie, removing');
            
            const cookieDomain = window.location.hostname.includes('apprendo.co') ? '.apprendo.co' : undefined;
            const cookieSettings = cookieDomain ? `; domain=${cookieDomain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; secure; samesite=none` : '; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; secure';
            
            document.cookie = `${name}=${cookieSettings}`;
            cleanupPerformed = true;
          }
        }
        
        // Clear any code verifier cookies too
        if (name === `${COOKIE_NAME}-code-verifier`) {
          const cookieDomain = window.location.hostname.includes('apprendo.co') ? '.apprendo.co' : undefined;
          const cookieSettings = cookieDomain ? `; domain=${cookieDomain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; secure; samesite=none` : '; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; secure';
          
          document.cookie = `${name}=${cookieSettings}`;
          cleanupPerformed = true;
        }
      }
    } catch (e) {
      logger.warn('Error processing cookies', e);
    }
    
    // 3. Set a flag to prevent running this cleanup again in this session
    try {
      if (cleanupPerformed) {
        sessionStorage.setItem('auth_cleanup_performed', 'true');
        logger.info('Auth cleanup completed successfully');
      } else {
        logger.info('No legacy auth tokens found, clean state');
      }
    } catch (e) {
      logger.warn('Error accessing sessionStorage', e);
    }
    
    return cleanupPerformed;
  } catch (error) {
    logger.error('Unexpected error in auth cleanup', error);
    return false;
  }
}

/**
 * Detects if a user has a legacy token format
 * This can be used to determine if a user needs to go through session reset
 */
export function hasLegacyTokenFormat(): boolean {
  try {
    // Get the project ref from environment or fallback to hardcoded value
    const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL!;
    const PROJECT_REF = SUPABASE_URL.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1] || 'lpjvndsvkvqgkdkvacnn';
    
    // Get proper cookie name format
    const COOKIE_NAME = `sb-${PROJECT_REF}-auth-token`;
    
    // Check localStorage first
    try {
      const storedToken = localStorage.getItem(COOKIE_NAME);
      
      if (storedToken) {
        // Check if it's in the legacy base64 format
        if (storedToken.includes('base64-eyJ')) {
          return true;
        }
        
        // Check if it's not proper JSON
        try {
          JSON.parse(storedToken);
        } catch (e) {
          // Not valid JSON
          return true;
        }
      }
    } catch (e) {
      logger.warn('Error accessing localStorage during legacy check', e);
    }
    
    // Then check cookies
    try {
      const cookies = document.cookie.split(';');
      
      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        
        if (name === COOKIE_NAME) {
          // Try to decode the cookie value
          try {
            const decodedValue = decodeURIComponent(value);
            
            // Check if it's in legacy format
            if (decodedValue.includes('base64-eyJ')) {
              return true;
            }
            
            // Try parsing it as JSON to check validity
            try {
              JSON.parse(decodedValue);
            } catch (e) {
              // Not valid JSON
              return true;
            }
          } catch (e) {
            // If we can't decode it, it's probably malformed
            return true;
          }
        }
      }
    } catch (e) {
      logger.warn('Error processing cookies during legacy check', e);
    }
    
    // Check if any of the legacy keys exist
    const legacyKeys = [
      'auth_token',
      'auth_refresh_token',
      'auth_expires_at',
      'last_token_refresh',
      'auth_access_token',
      'auth_profile'
    ];
    
    for (const key of legacyKeys) {
      if (localStorage.getItem(key)) {
        return true;
      }
    }
    
    // No legacy tokens found
    return false;
  } catch (error) {
    logger.error('Unexpected error checking for legacy tokens', error);
    return false;
  }
}

/**
 * Registers the cleanup function to run on page load
 * Should be called from a client component near the root of the app
 */
export function registerAuthCleanup() {
  if (typeof window !== 'undefined') {
    // Check if we've already run cleanup in this session
    if (sessionStorage.getItem('auth_cleanup_performed') === 'true') {
      logger.debug('Auth cleanup already performed in this session, skipping');
      return;
    }
    
    // Run cleanup
    cleanupLegacyAuthTokens();
  }
}

export default { cleanupLegacyAuthTokens, hasLegacyTokenFormat, registerAuthCleanup };